/**
 * Shared fetch + ApiResponse unwrap used by HttpRepository methods.
 */

import { ApiResponseSchema } from '@meridian/contracts';
import { DatasetNotFoundError } from '@meridian/core';

import { DataError, mapApiErrorCode } from './errors';

import type { z } from 'zod';

function requestCredentials(url: string): RequestCredentials {
  if (typeof window === 'undefined') return 'same-origin';
  return new URL(url, window.location.href).origin === window.location.origin
    ? 'same-origin'
    : 'include';
}

function unwrapEnvelope<T>(
  res: Response,
  body: unknown,
  schema: z.ZodType<T>,
  notFound: { readonly resource: 'security' | 'sector'; readonly id: string } | undefined,
): T {
  const envelope = ApiResponseSchema(schema).safeParse(body);
  if (!envelope.success)
    throw new DataError('VALIDATION', 'Response failed schema validation', envelope.error);
  const value = envelope.data;
  if (value.success) {
    if (!res.ok) throw new DataError('UNKNOWN', `Unexpected HTTP status (${String(res.status)})`);
    return value.data;
  }
  const code = mapApiErrorCode(value.error.code);
  if (value.error.code === 'NOT_FOUND' && notFound !== undefined) {
    throw new DatasetNotFoundError(notFound.resource, notFound.id);
  }
  if (value.error.code === 'SECURITY_DELISTED') {
    throw new DataError(code, value.error.message, undefined, {
      ...(value.error.canonicalTicker === undefined
        ? {}
        : { canonicalTicker: value.error.canonicalTicker }),
      ...(value.error.lastSeenOn === undefined ? {} : { lastSeenOn: value.error.lastSeenOn }),
    });
  }
  throw new DataError(code, value.error.message);
}

export async function httpGetJson<T>(
  fetchImpl: typeof fetch,
  url: string,
  schema: z.ZodType<T>,
  notFound?: { readonly resource: 'security' | 'sector'; readonly id: string },
): Promise<T> {
  let res: Response;
  try {
    res = await fetchImpl(url, {
      credentials: requestCredentials(url),
      headers: { Accept: 'application/json' },
    });
  } catch (cause) {
    throw new DataError('NETWORK', 'Network request failed', cause);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (cause) {
    throw new DataError('UNKNOWN', `Non-JSON response (${String(res.status)})`, cause);
  }

  return unwrapEnvelope(res, body, schema, notFound);
}

export function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    sp.set(k, String(v));
  }
  const q = sp.toString();
  return q.length > 0 ? `?${q}` : '';
}
